import { configureApi } from "./api.ts";
import { closePlayer } from "./player.ts";
import { dismissHoverCard } from "./components.ts";
import { clearAlerts } from "./dom.ts";
import { showShortcuts } from "./shortcuts.ts";
import { el, logoImage } from "./dom.ts";
import { icons } from "./icons.ts";
import { getState, updateSettings } from "./store.ts";
import { visibleProviders } from "./visibility.ts";
import { categoriesView, categoryTitle } from "./categories.ts";
import { customizeView } from "./feed-view.ts";
import { feedMasonryView } from "./feed-page.ts";
import { newView, statsView, trendingView } from "./pages-feed.ts";
import { globalSearchView } from "./search-views.ts";
import {
  detailView,
  gateView,
  homeView,
  libraryView,
  loadProviders,
  providerView,
  providersView,
  settingsView,
  type AppContext,
} from "./views.ts";

const app = document.getElementById("app") as HTMLElement;

const NAV_ITEMS = [
  { label: "Home", route: "#/home", icon: icons.home },
  { label: "Feed", route: "#/feed", icon: icons.grid },
  { label: "Providers", route: "#/providers", icon: icons.more },
  { label: "Search", route: "#/search", icon: icons.search },
  { label: "Categories", route: "#/categories", icon: icons.grid },
  { label: "Library", route: "#/library", icon: icons.library },
  { label: "Settings", route: "#/settings", icon: icons.settings },
];

/** The route link carries the name; the glyph is decoration. */
function navLink(item: (typeof NAV_ITEMS)[number]): HTMLElement {
  const link = el("a", { class: "route brand-route", attrs: { href: item.route } });
  link.append(
    el("span", { class: "nav-icon", attrs: { "aria-hidden": "true" }, html: item.icon }),
    el("span", { text: item.label }),
  );
  return link;
}

let context: AppContext;
let drawerOpen = false;
let paintDensityToggle: () => void = () => {};

document.addEventListener("aiox:settings", () => {
  paintDensityToggle();
});

function isActive(route: string, target: string): boolean {
  const path = route.replace(/^#/, "").split("?")[0];
  const want = target.replace(/^#/, "");
  if (want === "/home") return path === "/" || path === "/home";
  return path.startsWith(want);
}

function header(currentRoute: string): HTMLElement {
  const menu = el("button", {
    class: "brand-menu",
    attrs: {
      type: "button",
      "aria-label": "Open menu",
      "aria-expanded": String(drawerOpen),
      "aria-controls": "drawer",
    },
    html: icons.menu,
  });
  menu.addEventListener("click", () => {
    drawerOpen = true;
    render();
  });

  const wordmark = el("button", {
    class: "wordmark",
    attrs: { type: "button", "aria-label": "AIOX home" },
  });
  wordmark.append(
    el("img", {
      class: "brand-lockup",
      attrs: { src: "/logo.svg", alt: "", width: "95", height: "24" },
    }),
  );
  wordmark.addEventListener("click", () => context.navigate("#/home"));

  const primary = el("nav", { class: "primary-nav", attrs: { "aria-label": "Primary" } });
  for (const item of NAV_ITEMS) {
    const link = navLink(item);
    if (isActive(currentRoute, item.route)) link.setAttribute("aria-current", "page");
    link.addEventListener("click", (event) => {
      event.preventDefault();
      context.navigate(item.route);
    });
    primary.append(link);
  }

  const input = el("input", {
    class: "brand-search-input",
    attrs: {
      type: "search",
      name: "q",
      placeholder: "Search the catalogue",
      "aria-label": "Search the catalogue from the header",
      autocomplete: "off",
      spellcheck: "false",
      enterkeyhint: "search",
    },
  });
  input.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const query = input.value.trim();
    if (!query) return;
    context.navigate(`#/search?q=${encodeURIComponent(query)}`);
  });
  const search = el(
    "label",
    { class: "brand-search" },
    el("span", { class: "brand-search-icon", html: icons.search }),
    input,
  );

  const densityButton = el("button", { class: "coin top-toggle", attrs: { type: "button" } });
  paintDensityToggle = () => {
    const compact = getState().settings.density === "compact";
    densityButton.setAttribute("aria-pressed", String(compact));
    densityButton.setAttribute(
      "aria-label",
      compact ? "Use comfortable poster size" : "Use compact poster size",
    );
    densityButton.innerHTML = icons.density;
  };
  paintDensityToggle();
  densityButton.addEventListener("click", () => {
    updateSettings({ density: getState().settings.density === "compact" ? "comfortable" : "compact" });
    paintDensityToggle();
  });

  return el(
    "header",
    { class: "site-header" },
    el(
      "div",
      { class: "container site-header-inner" },
      el("div", { class: "brand-left" }, menu, wordmark),
      primary,
      el("div", { class: "header-tools" }, densityButton, search),
    ),
  );
}

function drawer(currentRoute: string): HTMLElement {
  const close = el("button", {
    class: "coin",
    attrs: { type: "button", "aria-label": "Close menu" },
    html: icons.close,
  });
  close.addEventListener("click", () => closeDrawer());

  const nav = el("nav", { class: "drawer-nav", attrs: { "aria-label": "Routes" } });
  for (const item of NAV_ITEMS) {
    const link = el("a", { class: "drawer-link", attrs: { href: item.route } });
    link.append(
      el("span", { class: "nav-icon", attrs: { "aria-hidden": "true" }, html: item.icon }),
      el("span", { text: item.label }),
    );
    if (isActive(currentRoute, item.route)) link.setAttribute("aria-current", "page");
    link.addEventListener("click", (event) => {
      event.preventDefault();
      closeDrawer();
      context.navigate(item.route);
    });
    nav.append(link);
  }

  const providers = el("div", { class: "drawer-providers" });
  const groups = new Map<string, typeof context.providers>();
  for (const provider of visibleProviders(context.providers)) {
    const bucket = groups.get(provider.category) ?? [];
    bucket.push(provider);
    groups.set(provider.category, bucket);
  }
  for (const [category, entries] of groups) {
    providers.append(el("p", { class: "drawer-group", text: categoryTitle(category) }));
    for (const provider of entries) {
      const link = el("a", {
        class: "drawer-provider",
        attrs: { href: `#/provider/${provider.id}` },
      });
      link.append(logoImage(provider.logo, provider.name), el("span", { text: provider.name }));
      if (provider.state === "offline") link.dataset.state = "offline";
      if (currentRoute === `#/provider/${provider.id}`) link.setAttribute("aria-current", "page");
      link.addEventListener("click", (event) => {
        event.preventDefault();
        closeDrawer();
        context.navigate(`#/provider/${provider.id}`);
      });
      providers.append(link);
    }
  }

  return el(
    "aside",
    {
      class: "drawer",
      attrs: {
        id: "drawer",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": "Catalogue menu",
      },
    },
    el(
      "div",
      { class: "drawer-head" },
      el("img", {
        class: "brand-lockup",
        attrs: { src: "/logo.svg", alt: "AIOX", width: "95", height: "24" },
      }),
      close,
    ),
    nav,
    providers,
  );
}

function scrim(): HTMLElement {
  const node = el("div", { class: "drawer-scrim", attrs: { "aria-hidden": "true" } });
  node.addEventListener("click", () => closeDrawer());
  return node;
}

function closeDrawer(): void {
  drawerOpen = false;
  render();
  document
    .querySelector<HTMLElement>(".site-header .brand-menu")
    ?.focus({ preventScroll: true });
}

function footer(): HTMLElement {
  const working = visibleProviders(context.providers).filter(
    (provider) => provider.state !== "offline",
  );
  const categories = new Set(working.map((provider) => provider.category));
  const links = el("nav", { class: "footer-nav", attrs: { "aria-label": "Footer" } });
  for (const item of NAV_ITEMS.slice(0, 5)) {
    const link = el("a", { class: "footer-link", attrs: { href: item.route }, text: item.label });
    link.addEventListener("click", (event) => {
      event.preventDefault();
      context.navigate(item.route);
    });
    links.append(link);
  }
  const keyboard = el("button", {
    class: "footer-link footer-button",
    attrs: { type: "button" },
    text: "Keyboard",
  });
  keyboard.addEventListener("click", () => showShortcuts());

  const network = el("nav", { class: "footer-nav", attrs: { "aria-label": "Network" } });
  for (const item of [
    { label: "New", route: "#/new" },
    { label: "Trending", route: "#/trending" },
    { label: "Stats", route: "#/stats" },
  ]) {
    const link = el("a", { class: "footer-link", attrs: { href: item.route }, text: item.label });
    link.addEventListener("click", (event) => {
      event.preventDefault();
      context.navigate(item.route);
    });
    network.append(link);
  }
  network.append(keyboard);
  return el(
    "footer",
    { class: "site-footer" },
    el(
      "div",
      { class: "container site-footer-inner" },
      el(
        "div",
        { class: "footer-brand" },
        el("img", {
          class: "brand-lockup",
          attrs: { src: "/logo.svg", alt: "AIOX", width: "95", height: "24" },
        }),
        el("p", {
          class: "footer-line",
          text: `${working.length} working providers in ${categories.size} categories, read live.`,
        }),
      ),
      el("div", { class: "footer-links" }, links, network),
    ),
  );
}

/**
 * Unknown hashes and the explicit #/404 route share one page: the shell keeps
 * the brand, the page says what happened in one line, and one link goes back.
 */
function notFoundView(): HTMLElement {
  return el(
    "div",
    { class: "view view--not-found" },
    el(
      "div",
      { class: "not-found" },
      el("p", { class: "not-found-code", text: "404", attrs: { "aria-hidden": "true" } }),
      el("h1", { class: "not-found-title", text: "Page not found" }),
      el("p", {
        class: "not-found-line",
        text: "That address is not part of the catalogue.",
      }),
      el("a", {
        class: "btn btn-primary",
        attrs: { href: "#/home" },
        text: "Back to the catalogue",
      }),
    ),
  );
}

function parseRoute(): { path: string; params: string[]; query: URLSearchParams } {
  const raw = window.location.hash.replace(/^#/, "") || "/home";
  const [path, queryString] = raw.split("?");
  return {
    path: path || "/home",
    // The hash segment is percent-encoded by the router; decode once so the
    // API client can encode it again for the request path.
    params: path
      .split("/")
      .filter(Boolean)
      .map((part) => decodeURIComponent(part)),
    query: new URLSearchParams(queryString ?? ""),
  };
}

function render(): void {
  const route = parseRoute();
  const main = el("main", { class: "main", attrs: { id: "main", tabindex: "-1" } });

  let view: HTMLElement;
  switch (route.params[0] ?? "home") {
    case "home":
      view = homeView(context);
      break;
    case "provider":
      view = providerView(context, route.params[1] ?? "");
      break;
    case "watch":
      view = detailView(context, route.params[1] ?? "", route.params[2] ?? "");
      break;
    case "search":
      view = globalSearchView(
        context,
        route.query.get("q") ?? "",
        (route.query.get("sites") ?? "").split(",").filter(Boolean),
      );
      break;
    case "providers":
      view = providersView(context);
      break;
    case "categories":
      view = categoriesView(context);
      break;
    case "feed":
      view = feedMasonryView(context);
      break;
    case "customize":
      view = customizeView(context);
      break;
    case "library":
      view = libraryView(context);
      break;
    case "settings":
      view = settingsView(context);
      break;
    case "new":
      view = newView(context);
      break;
    case "trending":
      view = trendingView(context);
      break;
    case "stats":
      view = statsView(context);
      break;
    case "404":
      view = notFoundView();
      break;
    default:
      view = notFoundView();
  }
  main.append(view);
  app.replaceChildren(header(`#${route.path}`), main, footer());

  if (drawerOpen) {
    app.append(scrim(), drawer(`#${route.path}`));
    document.body.dataset.drawer = "true";
    requestAnimationFrame(() => {
      app.querySelector<HTMLElement>(".drawer .coin")?.focus({ preventScroll: true });
    });
  } else {
    delete document.body.dataset.drawer;
  }
}

async function loadPreferredProviders(): Promise<string[]> {
  try {
    const response = await fetch("/coverage.json", { cache: "no-store" });
    if (!response.ok) return [];
    const coverage = (await response.json()) as Record<string, { deployed: boolean; local: boolean }>;
    return Object.entries(coverage)
      .filter(([, entry]) => entry.deployed || entry.local)
      .map(([id]) => id);
  } catch {
    return [];
  }
}

async function boot(): Promise<void> {
  await configureApi();
  const providers = await loadProviders();
  const preferred = await loadPreferredProviders();
  context = {
    providers,
    preferred,
    navigate: (path: string) => {
      clearAlerts();
      if (window.location.hash === path) {
        window.scrollTo({ top: 0 });
        return;
      }
      window.location.hash = path;
      window.scrollTo({ top: 0 });
    },
  };

  if (!getState().gateAccepted) {
    // The gate is modal: the catalogue behind it is inert until it is answered,
    // so Tab and screen readers cannot reach a page that is not readable yet.
    const app = document.getElementById("app");
    if (app) app.inert = true;
    document.body.append(gateView());
  }

  // A connection that drops should say so once, not surface as a dozen empty
  // rows. The banner is a live region so it is announced when it appears.
  const offline = el("p", {
    class: "offline-banner",
    attrs: { role: "status", "aria-live": "polite" },
    text: "You are offline. Open screens stay readable; new titles will not load.",
  });
  offline.hidden = navigator.onLine;
  const syncConnection = (): void => {
    offline.hidden = navigator.onLine;
  };
  window.addEventListener("online", syncConnection);
  window.addEventListener("offline", syncConnection);
  document.body.append(offline);

  window.addEventListener("hashchange", () => {
    // A player belongs to the title it was opened from; leaving that route
    // should never leave a paused overlay over the next page.
    closePlayer();
    dismissHoverCard();
    clearAlerts();
    render();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && drawerOpen) {
      closeDrawer();
      return;
    }
    if (event.key === "Tab" && drawerOpen) {
      const surface = document.querySelector<HTMLElement>(".drawer");
      if (!surface) return;
      const focusable = [
        ...surface.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    const typing =
      event.target instanceof HTMLElement &&
      ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName);
    if (event.key === "?" && !typing && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      showShortcuts();
      return;
    }
    if (event.key !== "/" || event.metaKey || event.ctrlKey) return;
    const target = event.target as HTMLElement | null;
    if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
    const searchInput = document.querySelector<HTMLInputElement>(".brand-search-input");
    if (!searchInput) {
      if (drawerOpen) closeDrawer();
      context.navigate("#/search");
      return;
    }
    event.preventDefault();
    searchInput.focus();
  });
  render();
  app.removeAttribute("aria-busy");
  app.dataset.ready = "true";
}

void boot();
